/**
 * [PURPOSE]: Sub-plan 2A — composer kind→catalog dispatch. Returns a
 *   normalized catalog list `{ id, label, ...meta }` for a given picker kind.
 *   Read by ReferencePicker; lives one layer below the picker so kinds can
 *   be swapped without touching the picker UI.
 *
 * [WHY]: Pre-2A every picker duplicated its own catalog source — LanguagePicker
 *   has inline DND_LANGUAGES, CustomFeatCreator has 6 PREREQ_* arrays, race +
 *   subclass + spell creators each hand-rolled SRD enumerations. The registry
 *   centralizes catalog sourcing so:
 *     - Sourcebook additions (Sub-plan 3D) declare new types here ONCE; every
 *       picker that asks for that kind inherits the new entries automatically.
 *     - Custom + SRD content union'd in one place (kind=class/race/feat/spell).
 *     - Tests assert kind dispatch without driving every picker UI surface.
 *
 * [INTEGRATION]:
 *   - ReferencePicker reads via getCatalog(kind, playerStats).
 *   - Sub-plan 3D extends getCatalog to merge `playerStats.sources[].declared*`
 *     declarations into the result for sourcebook-aware kinds.
 *
 * [RULE 22]: Reads from src/data/customFeatVocabulary.js (canonical vocab).
 *   No inline replicas — the registry is the dispatcher, vocab is the data.
 *
 * @typedef {Object} CatalogEntry
 * @property {string} id - canonical key the runtime uses for lookups
 * @property {string} label - display string
 * @property {Object} [meta] - kind-specific extra (e.g. { hasRange: true } for senses)
 */

import {
  PREREQ_SKILLS,
  PREREQ_TOOLS,
  PREREQ_ARMOR,
  PREREQ_WEAPONS,
  PREREQ_SAVES,
  PREREQ_LANGUAGES,
  ALL_PREREQ_PROFICIENCIES,
  ADVANTAGE_TARGETS,
  SENSE_TYPES,
  DAMAGE_TYPES_FEAT,
  DAMAGE_SOURCE_TYPES,
  RECHARGE_OPTIONS,
  CREATURE_SIZES,
  DIE_SIZES,
  CLASS_HIT_DICE,
  SPELL_SCHOOLS,
  CASTING_TIMES,
  SPELL_RANGES,
  SPELL_DURATIONS,
  CASTER_TYPES,
  SPELL_PREP_TYPES,
  ITEM_RARITIES,
  ARMOR_TYPES_AC,
  STAT_MOD_TARGETS,
  WEAPON_BASE_TYPES,
  TARGET_TYPE_OPTIONS,
  AMMO_TYPE_OPTIONS,
  ALIGNMENT_TYPES,
  SPOKEN_LANGUAGES,
} from '../../data/customFeatVocabulary';
// [SUB-PLAN 1R — 2026-05-29]: Equipment slot vocabulary — all 26 EQUIPMENT_SLOTS
//   keys mapped through EQUIPMENT_SLOT_LABELS to { id, label } shape.
//   Sourced from enums.js (single truth); iterates EQUIPMENT_SLOT_LABELS whose
//   keys ARE the slot values so Object.entries yields the correct id→label pairs.
import { EQUIPMENT_SLOT_LABELS } from '../../constants/enums';
import { SRD_CLASSES, SRD_RACES, SRD_BACKGROUNDS } from '../../data/srdCharacterData';
import { SRD_FEATS } from '../../data/srdFeats';
import { SRD_SPELLS } from '../../data/srdSpells';
import { SRD_CONDITIONS } from '../../data/srdConditions';
// [STONE B — 2026-06-07 · 2M-EDITOR-CLOSED-SELECTS]: CR vocabulary single-source.
//   CR_ANCHORS = Object.freeze(CR_TABLE) (DMG p.274, 34 entries). crCalculator has
//   no composer import → no circular dependency. The `cr` kind maps this to {id,label}.
import { CR_ANCHORS } from '../../services/crCalculator';

// ============================================================================
// Per-kind static catalogs
// ============================================================================
// These are pre-baked; no playerStats dependency. ReferencePicker tests assert
// each returns a non-empty normalized list of { id, label } shape.

const KIND_CATALOGS = {
  skill: PREREQ_SKILLS,
  tool: PREREQ_TOOLS,
  armor: PREREQ_ARMOR,
  weapon: PREREQ_WEAPONS,
  save: PREREQ_SAVES,
  language: PREREQ_LANGUAGES,
  proficiency: ALL_PREREQ_PROFICIENCIES,
  advantageTarget: ADVANTAGE_TARGETS.map(({ value, label }) => ({ id: value, label })),
  sense: SENSE_TYPES.map(({ value, label, hasRange }) => ({ id: value, label, meta: { hasRange } })),
  damageType: DAMAGE_SOURCE_TYPES.map((dt) => ({ id: dt, label: dt.charAt(0).toUpperCase() + dt.slice(1) })),
  damageResistanceType: DAMAGE_TYPES_FEAT.map((dt) => ({ id: dt, label: dt.charAt(0).toUpperCase() + dt.slice(1) })),
  ability: [
    { id: 'str', label: 'Strength' },
    { id: 'dex', label: 'Dexterity' },
    { id: 'con', label: 'Constitution' },
    { id: 'int', label: 'Intelligence' },
    { id: 'wis', label: 'Wisdom' },
    { id: 'cha', label: 'Charisma' },
  ],
  // [SUB-PLAN 2F — 2026-05-25]: Resource/ability recharge trigger. Used by
  //   CustomFeatCreator (activeAbility.rechargeOn, resourcePool.rechargeOn)
  //   and CustomClassCreator (resource.rechargeOn). 4 entries: the D&D SRD
  //   resource-reset triggers. Expands CustomClassCreator from 2 hardcoded
  //   options (short_rest/long_rest) to the full vocab.
  rechargeOn: RECHARGE_OPTIONS,
  // [SUB-PLAN 2F kind (c) — 2026-05-25]: D&D 5e creature/race/object size
  //   vocabulary. 6 entries: Tiny/Small/Medium/Large/Huge/Gargantuan.
  //   Title-Case ids match existing storage shapes in all 3 consumers — no
  //   data migration needed. Consumers: MonsterEditor basics.size,
  //   CustomRaceCreator race.size (expanded 2→6), CustomSpellCreator
  //   material-component newComponent.size.
  creatureSize: CREATURE_SIZES,
  // [SUB-PLAN 2F kind (d) — 2026-05-25]: Generic resource/damage die picker.
  //   5 entries: d4/d6/d8/d10/d12 (bare 'd{N}' shape). d20 excluded — check
  //   die, not a resource or damage die. Consumer: CustomFeatCreator
  //   feat.resourcePool.dieSize (L1597-1604). Retires inline ['d4','d6',...] array.
  //   Pre-emptive split from classHitDie (Rule 30c) — vocab size (5 vs 4),
  //   shape (bare vs '1d'-prefixed), and semantic domain all diverge.
  dieSize: DIE_SIZES,
  // [SUB-PLAN 2F kind (d) — 2026-05-25]: PHB-constrained class hit-die picker.
  //   4 entries: 1d6/1d8/1d10/1d12 ('1d{N}' prefixed shape). No 1d4 (too frail
  //   for any official 5e class); no 1d20 (not a class hit die). Consumer:
  //   CustomClassCreator classData.hitDice (L1488-1497). Retires HIT_DICE_OPTIONS
  //   const at L80. Storage shape '1d10' unchanged — no data migration needed.
  //   [BREADCRUMB]: MonsterEditor has no hit-die surface today (HP = free-text
  //   formula like '2d6+3'). When Sub-plan 2D Monster Lego Parity adds a
  //   structured hit-die picker, it should use a NEW kind 'monsterHitDie' —
  //   semantically distinct from class hit die and generic resource die.
  classHitDie: CLASS_HIT_DICE,
  // [SUB-PLAN 2F kind (e) — 2026-05-25]: Spell quartet — four primitive kinds for
  //   CustomSpellCreator. All use display-string-as-id (id===label), storage shape
  //   unchanged. Retires 4 inline const arrays in CustomSpellCreator.js.
  //
  //   PRE-EMPTIVE-SPLIT NOTE (Rule 30c): castingTime and spellDuration share several
  //   string values ('1 minute', '10 minutes', '1 hour', '8 hours', '24 hours') but
  //   are SEMANTICALLY DISTINCT: castingTime = COST to cast (how long casting takes);
  //   spellDuration = HOW LONG the effect persists. They get separate kinds.
  spellSchool: SPELL_SCHOOLS,
  castingTime: CASTING_TIMES,
  spellRange: SPELL_RANGES,
  spellDuration: SPELL_DURATIONS,
  // [SUB-PLAN 2F kind (f) — 2026-05-25]: CustomClassCreator spellcasting pickers.
  //   TWO kinds — semantic split is clean (Rule 30c):
  //
  //   kind='casterType': HOW a class gains spell slots (full / half / third / pact).
  //     4 entries, id===label (bare display strings). Storage: classData.spellcasting.spellSlots
  //     (field IS named spellSlots historically — its value is the caster-type string;
  //     rename cascades across save/load, out of scope). Consumer: CustomClassCreator
  //     L2321-2346 caster type <select> → ReferencePicker.
  //
  //   kind='spellPrepType': HOW a class manages its spell list (known / prepared / spellbook).
  //     3 entries, id ≠ label (ids: 'known'/'prepared'/'spellbook'; labels are verbose
  //     canonical-class references). Normalized from legacy { value, label } shape in
  //     CustomClassCreator to { id, label } per registry contract. Consumer:
  //     CustomClassCreator L2352-2369 spell prep <select> → ReferencePicker.
  //
  //   Helper extraction resolved in this commit (Audit Wave-5 category 4): both inline
  //   onChanges contained multi-statement side-effect logic → handleCasterTypeChange +
  //   handleSpellPrepTypeChange extracted BEFORE the picker swap.
  casterType: CASTER_TYPES,
  spellPrepType: SPELL_PREP_TYPES,
  // [SUB-PLAN 2F kind (g) — 2026-05-25]: FINAL 2F stone — two ItemDialog kinds.
  //
  //   kind='rarity': 7-tier magic item rarity vocabulary. id ≠ label — ids are
  //     lowercase snake_case ('very_rare'), labels are Title Case ('Very Rare').
  //     Empty-string id preserves "No Rarity (Mundane)" for mundane items.
  //     Consumer: ItemDialog rarity <select> → ReferencePicker (pure setter onChange).
  //     Vocab source: ITEM_RARITIES (named with ITEM_ prefix for disambiguation).
  //
  //   kind='armorType': 4 AC-computation armor types. id ≠ label — ids are bare
  //     lowercase ('light'), labels include verbose AC formula ('Light (AC = DEX + base, no cap)').
  //     SEMANTIC SPLIT (Rule 30c): DISTINCT from kind='armor' (proficiency categories
  //     with _armor suffix, 'light_armor'/'medium_armor'). This kind answers
  //     "what's the AC formula?"; kind='armor' answers "can you wear it?".
  //     Consumer: ItemDialog armorType <select> → ReferencePicker (handleArmorTypeChange).
  //     Vocab source: ARMOR_TYPES_AC (named with _AC suffix to disambiguate from PREREQ_ARMOR).
  //
  //   This commit CLOSES Sub-plan 2F — 13 composer kinds total shipped across 7 commits:
  //   rechargeOn, condition, creatureSize, dieSize, classHitDie, spellSchool,
  //   castingTime, spellRange, spellDuration, casterType, spellPrepType, rarity, armorType.
  rarity: ITEM_RARITIES,
  armorType: ARMOR_TYPES_AC,
  // [STONE A — 2026-06-05]: monster/NPC alignment vocabulary. 15 entries:
  //   9 canonical + Unaligned + the 5 SRD-attested "Any …" meta-alignments
  //   (open5e-verified; non-SRD meta-alignments + split prose go through allowMint).
  //   id === label (alignment stored as the display string on monster.alignment).
  //   Consumer (Rule 30a same-commit): MonsterEditor alignment field →
  //   ReferencePicker kind="alignment" allowMint (the "+ add" handles genuine
  //   split-probability prose without an always-on textbox). Retires the
  //   hardcoded ALIGNMENTS const in MonsterEditor.js. No data migration
  //   (storage shape unchanged); 2 baseMonsters case-variants normalized same arc.
  alignment: ALIGNMENT_TYPES,
  // [STONE A pt2 — 2026-06-07 · 2M-EDITOR-VOCAB-PICKERS]: creature SPOKEN-language
  //   vocabulary for MonsterEditor's languages field. Bare Title-case id===label
  //   matches the stored `monster.languages` string[] shape. DISTINCT from the
  //   prefixed-lowercase feat-prereq `language` kind (PREREQ_LANGUAGES) above —
  //   semantically distinct domains, kind-split per Rule 30c. allowMint at the
  //   consumer covers telepathy/prose. [RSE seam → communicationMode generalization.]
  spokenLanguage: SPOKEN_LANGUAGES,
  // [STONE B — 2026-06-07 · 2M-EDITOR-CLOSED-SELECTS]: Challenge Rating — the 34
  //   fully-closed CR values (DMG p.274). Sourced from CR_ANCHORS (= CR_TABLE) so the
  //   picker and crCalculator share ONE source (Rule 22, no inline replica). id===label
  //   bare cr string ('0','1/8','1/4','1/2','1'..'30') matches stored monster.cr.
  //   Fully closed → MonsterEditor uses it single-mode WITHOUT allowMint. [RSE seam →
  //   challengeRating/difficultyTier generalization; Daggerheart Tier, CoC has none.]
  cr: CR_ANCHORS.map((r) => ({ id: r.cr, label: r.cr })),
  // [ITEM SHAPE HARMONIZATION — Stone 1 — 2026-05-25]: item stat-modification target picker.
  //   18 entries: set/add/subtract × 6 ability scores. IDs match the storage strings written
  //   by ItemDialog (statBonus field) and parsed by itemEffectsRegistration.js emitAbilityRow
  //   regex `/^(set|add|subtract)(.+)$/i` — no data migration needed.
  //   `meta.group` on each entry drives ReferencePicker optgroup rendering (3 groups:
  //   'Set Stat To' / 'Add To Stat' / 'Subtract From Stat') matching ItemDialog L1237-1260.
  //   Consumer (Rule 30a): StatModificationsList.jsx (Stone 2, same commit).
  //   Deferred consumers: ItemDialog (Stone 4), EditItemDialog (Stone 5).
  statModTarget: STAT_MOD_TARGETS,
  // [SUB-PLAN 1R — 2026-05-29]: All 26 equipment-slot values. IDs are the slot
  //   strings used throughout the runtime (gameData.js, InventoryContext dispatch,
  //   EquipmentPanel slot keys — e.g., 'mainhand', 'ring1', 'ioun', 'misc').
  //   Source: EQUIPMENT_SLOT_LABELS from enums.js — keys ARE the slot values,
  //   values ARE the display labels. Iterating Object.entries gives { id, label }
  //   directly. Includes 'ioun' (Ioun Stone orbits head) and 'misc' (no equip).
  //   Consumer (Rule 30a): ItemDialog equip-slot field → ReferencePicker (Stone 2,
  //   same commit). EditItemDialog deferred — breadcrumb in master-queue 1R-eid.
  equipSlot: Object.entries(EQUIPMENT_SLOT_LABELS).map(([value, label]) => ({ id: value, label })),
  // [SUB-PLAN 1H-H-cond Phase 2 — 2026-05-29 — TASK 1 weaponkind]:
  //   37-entry SRD weapon base-type vocabulary. Ids are BARE baseType keys
  //   matching BASE_ITEMS.weapons in gameData.js (e.g. 'longbow', 'shortsword').
  //   The existing 'weapon' kind = proficiency categories ('weapon:simple' /
  //   'weapon:martial') — semantically DISTINCT domain (Rule 30c split).
  //   Consumer: ConditionKindSlot kind='weaponType' operand → ReferencePicker
  //   kind="weaponBaseType" multi (same commit). Runtime evaluator compares
  //   context.equippedWeaponBaseType against these bare ids.
  //   [BREADCRUMB]: Custom weapon base types (authored via Content Workshop
  //   custom weapon items) are not yet surfaced here — deferred to the
  //   endgame generalization arc when custom weapon creation lands.
  weaponBaseType: WEAPON_BASE_TYPES,
  // [SUB-PLAN 1H-H-cond Phase 2 — 2026-05-29 — TASK 2 trigfilters]:
  //   14 SRD creature/monster types. Used as the constrained pick list for
  //   the triggeredEffect targetTypeMatch condition filter (replacing free-text
  //   <input>). Values match what _readTriggerContextField('targetTypeMatch')
  //   reads from context.target.type / context.targetType at fire-time.
  //   Ids are normalized from TARGET_TYPE_OPTIONS { value, label } → { id, label }
  //   using the value field as id (same token the runtime uses).
  //   Consumer: EffectBuilderControls.js targetTypeMatch filter → constrained select
  //   (same commit). ConditionKindSlot targetType operand uses this kind via
  //   ReferencePicker for parity.
  //   [CONSUMED 2026-06-13 · R3-A1]: creatureType now serves the Monster Editor
  //   Basics "Creature Type" picker (MonsterEditor.js ~L878, ReferencePicker
  //   kind='creatureType', case-adapter lower↔Title). Rule-27 visual-verified
  //   2026-06-13 (e2e/screenshots/2026-06-13-rule27-verify/06-*). The same
  //   vocabulary still covers future per-system monster authoring (Sub-plan 2D).
  creatureType: TARGET_TYPE_OPTIONS.map(({ value, label }) => ({ id: value, label })),
  // [SUB-PLAN 1H-H-cond Phase 2 — 2026-05-29 — TASK 2 trigfilters]:
  //   4-entry ammo-type vocabulary. Ids are the ammoType tokens written by
  //   gameData.js weapon+ammo entries (arrows / crossbow_bolts / sling_bullets /
  //   blowgun_needles). Used as the constrained pick list for the triggeredEffect
  //   ammoTypeMatch condition filter. Replaces the free-text <input> that let
  //   authors type 'arrow' when the runtime uses 'arrows' (silent mismatch).
  //   Consumer: EffectBuilderControls.js ammoTypeMatch filter → constrained select.
  ammoType: AMMO_TYPE_OPTIONS,
};

// ============================================================================
// Composite-kind helpers (SRD + custom union)
// ============================================================================

function srdAsEntries(srdMap) {
  if (!srdMap || typeof srdMap !== 'object') return [];
  return Object.entries(srdMap).map(([key, val]) => ({
    id: (val && val.id) || key,
    label: (val && val.name) || key,
  }));
}

function customAsEntries(customArr) {
  if (!Array.isArray(customArr)) return [];
  return customArr
    .filter((c) => c && (c.id || c.name))
    .map((c) => ({
      id: c.id || c.name,
      label: c.name || c.id,
    }));
}

function dedupeById(entries) {
  const seen = new Set();
  const out = [];
  for (const e of entries) {
    if (e && e.id && !seen.has(e.id)) {
      seen.add(e.id);
      out.push(e);
    }
  }
  return out;
}

/**
 * Dispatch a picker kind to its catalog list. Returns [] for unknown kinds
 * (gracefully degrades — the picker shows nothing rather than crashing).
 *
 * Composite kinds (class/race/feat/spell) merge SRD + playerStats custom
 * (custom takes precedence on id collision). resourcePool is custom-only.
 *
 * @param {string} kind
 * @param {Object} [playerStats] - required for SRD+custom union kinds
 * @returns {CatalogEntry[]} normalized catalog list
 */
export function getCatalog(kind, playerStats = null) {
  if (KIND_CATALOGS[kind]) {
    return KIND_CATALOGS[kind];
  }
  switch (kind) {
    case 'class':
      return dedupeById([
        ...customAsEntries(playerStats && playerStats.customClasses),
        ...srdAsEntries(SRD_CLASSES),
      ]);
    case 'race':
      return dedupeById([
        ...customAsEntries(playerStats && playerStats.customRaces),
        ...srdAsEntries(SRD_RACES),
      ]);
    case 'feat':
      return dedupeById([
        ...customAsEntries(playerStats && playerStats.customFeats),
        ...srdAsEntries(SRD_FEATS),
      ]);
    case 'spell': {
      // [FORK-E 2026-05-31]: SRD_SPELLS is level-keyed ({ 0: [...], 1: [...], ...9: [...] }),
      //   NOT a flat { id, name } map. srdAsEntries() expected the flat map shape —
      //   passing SRD_SPELLS produced 10 entries with ids "0".."9" and labels undefined.
      //   Fix: flatten all level arrays into { id: spell.name, label: spell.name } entries.
      //   Level-0 cantrips are INCLUDED here; the authoring picker filters them client-side
      //   via the `filterLevel` prop (below). No change to entry shape — same { id, label }.
      //
      // [BREADCRUMB RSE — 2026-05-31]: `spellList` (FORK-E) generalizes to
      //   `powerPool: { type: 'spell'|'ability'|'prayer', entries: string[] }` when the
      //   Rule System Editor ships (multi-system arc). This catalog kind becomes the D&D
      //   specialization of a system-agnostic powerPool picker. See COMING_SOON_ROADMAP
      //   §FORK-E-breadcrumb for the tracking entry.
      const srdSpellFlat = Object.values(SRD_SPELLS)
        .flat()
        .map((s) => ({ id: s.name, label: s.name }));
      return dedupeById([
        ...customAsEntries(playerStats && playerStats.customSpells),
        ...srdSpellFlat,
      ]);
    }
    case 'resourcePool':
      return customAsEntries(playerStats && playerStats.customResourcePools);
    case 'condition': {
      // [SUB-PLAN 2F kind (b) — 2026-05-25]: D&D 5e binary conditions
      //   (SRD_CONDITIONS — 14 entries). Custom additions merge with SRD
      //   precedence on id collision (insertion order: custom first, dedupe
      //   drops the SRD duplicate). Consumers: ItemDialog conditionImmunities
      //   (Advanced Properties), MonsterEditor Defenses section. Unlocks
      //   Sub-plan 3B Condition Creator (custom-condition authoring UI).
      //
      //   [NS-C16 — 2026-05-31]: Exhaustion is NOT in SRD_CONDITIONS (it's a
      //   6-level numeric track, not a binary toggle) but ~14 baseMonsters
      //   entries carry 'exhaustion' in conditionImmunities[] and DMs authoring
      //   undead/constructs need to be able to select it. A synthetic entry is
      //   appended AFTER the SRD+custom union so the picker offers it without
      //   conflating it with binary toggle conditions. Backward-compatible —
      //   existing monsters with 'exhaustion' in conditionImmunities[] already
      //   round-trip correctly; this only surfaces the option in the picker.
      //   [BREADCRUMB RSE]: When the Rule System Editor lands, this synthetic
      //   entry is replaced by the RSE condition-kind catalog (Daggerheart
      //   has its own status-track types). See CLAUDE.md §4.11.
      const baseCatalog = dedupeById([
        ...customAsEntries(playerStats && playerStats.customConditions),
        ...srdAsEntries(SRD_CONDITIONS),
      ]);
      // Synthetic exhaustion entry — appended last so it doesn't displace SRD entries
      // and custom entries don't accidentally shadow it (its id is not in SRD_CONDITIONS).
      if (!baseCatalog.find((e) => e.id === 'exhaustion')) {
        baseCatalog.push({ id: 'exhaustion', label: 'Exhaustion', icon: '😴' });
      }
      return baseCatalog;
    }
    case 'boon':
      // [SUB-PLAN 3A — 2026-05-30]: Custom boons authored via CustomBoonCreator.
      //   Custom-only kind (no SRD boon catalog — boons are always DM/narrative-granted).
      //   Consumer (Rule 30a same-commit): useBoonEffects (registration hook in App.jsx).
      //   Forward-compat: any ReferencePicker that lets a user reference a boon by id
      //   (e.g., "This feature requires Boon of Invincibility") will use getCatalog('boon').
      //   [BREADCRUMB v1.x]: Boon id-reference pickers (prereqs, item unlock conditions)
      //   deferred — the ReferencePicker kind='boon' wiring is the forward-compat hook.
      return customAsEntries(playerStats && playerStats.customBoons);
    case 'background':
      // [SUB-PLAN 3C — 2026-05-30]: Custom backgrounds authored via CustomBackgroundCreator.
      //   Composite: custom-first, SRD fallback (Branch A narrative-only; Branch B mechanical
      //   deferred). Custom entry shape: { id, name, featureName, featureDescription,
      //   personalityTraits?, bonds?, ideals?, flaws? }.
      //   SRD shape: SRD_BACKGROUNDS[key] object — key becomes the id, key is also the label.
      //   Consumer (Rule 30a same-commit): CharacterCreationWizard step-4 picker and
      //   CharacterBasicsPanel background select are wired via getBackgroundList() from
      //   backgroundLookup.js. getCatalog('background', playerStats) is the registry-query
      //   surface; getBackgroundById(id, playerStats) is the per-id resolution surface.
      //   [BREADCRUMB RSE-BG v1.x]: When Rule System Editor generalises "background" to
      //   "character-context document", extend getCatalog('background', ps, { ruleSystem })
      //   to dispatch per-system catalogs (Daggerheart Experiences, CoC Occupations).
      //   Branch A storage shape already generalises; only the mechanical consumer layer
      //   needs a per-system resolver.
      return dedupeById([
        ...customAsEntries(playerStats && playerStats.customBackgrounds),
        ...srdAsEntries(SRD_BACKGROUNDS),
      ]);
    default:
      return [];
  }
}

/**
 * List every kind the registry knows about. Useful for diagnostics + composer
 * type-validation.
 *
 * @returns {string[]}
 */
export function listKnownKinds() {
  return Object.keys(KIND_CATALOGS).concat([
    'class',
    'race',
    'feat',
    'spell',
    'resourcePool',
    'condition',
    'boon',        // [SUB-PLAN 3A — 2026-05-30]: custom boons authored via CustomBoonCreator
    'background',  // [SUB-PLAN 3C — 2026-05-30]: custom + SRD backgrounds via CustomBackgroundCreator
  ]);
}

// [SUB-PLAN 1H-H-cond Phase 2]: expose new kind names for test assertions and
// diagnostics. The canonical list is always Object.keys(KIND_CATALOGS) —
// these constants are just typed aliases to avoid magic strings in tests.
export const KIND_WEAPON_BASE_TYPE = 'weaponBaseType';
export const KIND_CREATURE_TYPE    = 'creatureType';
export const KIND_AMMO_TYPE        = 'ammoType';
// [green-composer-C — 2026-05-29]: armorType is now the ConditionKindSlot consumer
// (closes bt2 breadcrumb — previously only ItemDialog used this kind).
export const KIND_ARMOR_TYPE       = 'armorType';

export default { getCatalog, listKnownKinds };
